import {
  NetworkStatus,
  UpdateQueryMapFn,
  useLazyQuery,
  useQuery
} from '@apollo/client';
import { useRef, useState } from 'react';

import {
  Ad,
  PageInfo,
  QueryResumesArgs,
  QueryVacanciesArgs,
  Vacancy
} from '@/__generated_graphql__/graphql';
import {
  Resume,
  ResumesQuery,
  ResumesQueryVariables,
  VacanciesQuery,
  VacanciesQueryVariables
} from '@/__generated_graphql__/types';
import { ParticipantEnum } from '@/constants/common';
import { GET_RESUMES, GET_VACANCIES } from '@/graphql/queries';
import { transformAdsToCards } from '@/utils/helpers/transformAdsToCard';

type AdsQuery = ResumesQuery | VacanciesQuery;
type AdsVariables = QueryVacanciesArgs | QueryResumesArgs;

type GetAdsQueryProps = {
  variables?: AdsVariables;
  skip?: boolean;
  participant: ParticipantEnum;
};

type ExtractedAdsData<T extends Ad = Ad> = {
  pageInfo: PageInfo | null;
  ads: T[];
  totalCount: number;
};

const extractAdsData = (data?: AdsQuery): ExtractedAdsData => {
  if (!data) return { ads: [], pageInfo: null, totalCount: 0 };

  if ('resumes' in data && data.resumes)
    return {
      ads: data.resumes.nodes as Resume[],
      pageInfo: data.resumes.pageInfo as PageInfo,
      totalCount: data.resumes.totalCount
    };

  if ('vacancies' in data && data.vacancies)
    return {
      ads: data.vacancies.nodes as Vacancy[],
      pageInfo: data.vacancies.pageInfo as PageInfo,
      totalCount: data.vacancies.totalCount
    };

  return { ads: [], pageInfo: null, totalCount: 0 };
};

export const mergeAdsData = (
  prev: AdsQuery,
  fetchMoreResult?: AdsQuery,
  loadingCallback?: (loading: boolean) => void
): ReturnType<UpdateQueryMapFn> => {
  try {
    if (!fetchMoreResult) return prev;

    if ('resumes' in fetchMoreResult && 'resumes' in prev) {
      const moreNodes = fetchMoreResult.resumes.nodes ?? [];
      const prevNodes = prev.resumes.nodes ?? [];

      // Even when the page is empty, adopt the new pageInfo: keeping the old
      // cursor/hasNextPage would refetch the same page in an endless loop.
      const ads = [...prevNodes, ...moreNodes] as Ad[];
      return {
        __typename: 'Query',
        resumes: {
          ...fetchMoreResult.resumes,
          nodes: ads.filter(
            (ad, index, self) => index === self.findIndex(t => t.id === ad.id)
          ),
          pageInfo: { ...fetchMoreResult.resumes.pageInfo }
        }
      };
    }

    if ('vacancies' in fetchMoreResult && 'vacancies' in prev) {
      const moreNodes = fetchMoreResult.vacancies.nodes ?? [];
      const prevNodes = prev.vacancies.nodes ?? [];

      const ads = [...prevNodes, ...moreNodes] as Ad[];
      return {
        __typename: 'Query',
        vacancies: {
          ...fetchMoreResult.vacancies,
          nodes: ads.filter(
            (ad, index, self) => index === self.findIndex(t => t.id === ad.id)
          ),
          pageInfo: { ...fetchMoreResult.vacancies.pageInfo }
        }
      };
    }

    return prev;
  } catch (error) {
    console.error('error during merge ads data', error);
  } finally {
    loadingCallback?.(false);
  }
};

const checkEqualObject = (
  prev: GetAdsQueryProps['variables'],
  current: GetAdsQueryProps['variables']
) => JSON.stringify(prev) === JSON.stringify(current);

const getLoading = (
  networkStatus: NetworkStatus
): { isLoading: boolean; isLoadingMore: boolean } => {
  const isLoadingMore =
    networkStatus === NetworkStatus.fetchMore ||
    networkStatus === NetworkStatus.refetch;

  const isLoading = networkStatus === NetworkStatus.loading;

  return { isLoading, isLoadingMore };
};

export const useAdsQuery = ({
  participant,
  variables = {}
}: GetAdsQueryProps) => {
  const prevVariables = useRef<GetAdsQueryProps['variables']>(variables);

  const [localeVariables, setLocaleVariables] = useState(variables);

  const isResumes = participant === ParticipantEnum.RESUME;

  const activeQuery = useQuery<AdsQuery, AdsVariables>(
    isResumes ? GET_RESUMES : GET_VACANCIES,
    {
      variables,
      notifyOnNetworkStatusChange: true
    }
  );

  const { loading, data, previousData, networkStatus, fetchMore, error } =
    activeQuery;

  const { ads, pageInfo, totalCount } = extractAdsData(
    networkStatus !== NetworkStatus.fetchMore && loading ? previousData : data
  );

  const { isLoadingMore, isLoading } = getLoading(networkStatus);

  const fetchMoreAds = () => {
    if (!pageInfo?.hasNextPage || isLoadingMore) return;

    fetchMore({
      variables: { ...localeVariables, after: pageInfo?.endCursor ?? null },
      updateQuery: (prev, { fetchMoreResult }) =>
        mergeAdsData(prev, fetchMoreResult)
    });
  };

  const handleRefetch = async (
    updatedVariables: QueryResumesArgs | QueryVacanciesArgs
  ) => {
    // Callers pass only { filter, sortBy } while the stored variables also
    // hold `first`, so compare merged shapes — otherwise an identical filter
    // still refetches and resets the cached pagination back to page one.
    const nextVariables = { ...prevVariables.current, ...updatedVariables };

    if (checkEqualObject(prevVariables.current, nextVariables)) return;

    activeQuery.refetch(nextVariables);
    setLocaleVariables(nextVariables);
    prevVariables.current = nextVariables;
  };

  return {
    hasMore: pageInfo?.hasNextPage ?? false,
    ads,
    totalCount,
    loading: isLoading,
    isLoadingMore,
    error,
    fetchMoreAds,
    refetch: handleRefetch
  };
};

export const useAdsLazyQuery = (
  adType: ParticipantEnum,
  variables: AdsVariables
) => {
  const [initialized, setInitialized] = useState<boolean>(false);

  const [fetchVacancies, vacanciesState] = useLazyQuery<
    VacanciesQuery,
    VacanciesQueryVariables
  >(GET_VACANCIES, { notifyOnNetworkStatusChange: true });

  const [fetchResumes, resumesState] = useLazyQuery<
    ResumesQuery,
    ResumesQueryVariables
  >(GET_RESUMES, { notifyOnNetworkStatusChange: true });

  const isResumes = adType === ParticipantEnum.RESUME;
  const activeFetch = isResumes ? fetchResumes : fetchVacancies;
  const activeState = isResumes ? resumesState : vacanciesState;

  const { data, loading, fetchMore, previousData, networkStatus } = activeState;
  const { isLoading, isLoadingMore } = getLoading(networkStatus);

  const fetchMoreAds = () => {
    try {
      const { pageInfo } = extractAdsData(data);
      const hasNextPage = pageInfo?.hasNextPage;

      if (!data) {
        return activeFetch({
          variables
        });
      }

      if (hasNextPage) {
        fetchMore({
          variables: { ...variables, after: pageInfo?.endCursor ?? null },
          updateQuery: (prev, { fetchMoreResult }) =>
            mergeAdsData(prev, fetchMoreResult)
        });
      }
    } finally {
      setInitialized(true);
    }
  };

  const { ads } = extractAdsData(
    networkStatus !== NetworkStatus.fetchMore && loading ? previousData : data
  );

  // Before the first fetch there is no pageInfo yet, but fetchMoreAds is the
  // one triggering the initial load — so report hasMore until data arrives.
  const hasMore = data
    ? (extractAdsData(data).pageInfo?.hasNextPage ?? false)
    : true;

  return {
    ads: transformAdsToCards(ads),
    fetchMore: fetchMoreAds,
    initialized,
    hasMore,
    loading: isLoading,
    fetchMoreLoading: isLoadingMore
  };
};
